import React from 'react';
import { useNavigate } from 'react-router-dom';
import P from '@hero/ui-kit/typography/P';
import CheckIcon from '@hero/ui-kit/icons/utility/CheckIcon';
import CaretIcon from '@hero/ui-kit/icons/utility/CaretIcon';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import Section from '@hero/ui-kit/layout/Section';
import useOutsideClick from '@hero/ui-kit/hooks/useOutsideClick';
import { useUserStatusContext } from '../../../context/UserStatus';
import useProviderNames from '../../../api/useProviderNames';
import ProviderDetails from '../ProviderDetails';
import * as Style from './style.module.scss';

const OrgSwitcher: React.FC = () => {
    const { orgs, activeOrgId, setActiveOrg } = useUserStatusContext();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = React.useState(false);
    const [dropdownRef, isOutsideClick] = useOutsideClick(isOpen);
    // Org names live in rtm (not user-status), so fan out one cached call per org.
    const providerNames = useProviderNames(orgs.map((org) => org.hero_organization_id));

    const orgLabel = React.useCallback(
        (heroOrgId: string) => providerNames[heroOrgId] || `Organization ${heroOrgId}`,
        [providerNames]
    );

    const sortedOrgs = React.useMemo(
        () =>
            [...orgs].sort((a, b) =>
                orgLabel(a.hero_organization_id).localeCompare(
                    orgLabel(b.hero_organization_id),
                    undefined,
                    { sensitivity: 'base' }
                )
            ),
        [orgs, orgLabel]
    );

    React.useEffect(() => {
        if (isOutsideClick) {
            setIsOpen(false);
        }
    }, [isOutsideClick]);

    if (!activeOrgId) {
        return null;
    }

    // Single-org users keep the static logo — there is nothing to switch between.
    if (orgs.length <= 1) {
        return <ProviderDetails herOrgId={activeOrgId} />;
    }

    const handleSelect = (heroOrgId: string) => {
        setIsOpen(false);
        if (heroOrgId === activeOrgId) {
            return;
        }
        setActiveOrg(heroOrgId);
        // Land on the default page with a clean URL — the previous org's filters /
        // pagination / any resource id in the path don't carry over to the new org.
        navigate('/');
    };

    return (
        <div className={Style.wrapper} ref={dropdownRef as React.RefObject<HTMLDivElement>}>
            <ClickableOpacity
                className={Style.trigger}
                alt="Switch organization"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                <ProviderDetails herOrgId={activeOrgId} />
                <CaretIcon direction={isOpen ? 'up' : 'down'} size="small" type="utility" />
            </ClickableOpacity>
            {isOpen && (
                <Section noDefaultPadding border className={Style.dropdown}>
                    {sortedOrgs.map((org) => {
                        const isActive = org.hero_organization_id === activeOrgId;
                        const label = orgLabel(org.hero_organization_id);

                        return (
                            <ClickableOpacity
                                key={org.hero_organization_id}
                                fullWidth
                                alt={`Switch to ${label}`}
                                className={`${Style.item} ${isActive ? Style.selected : ''}`}
                                onClick={() => handleSelect(org.hero_organization_id)}
                            >
                                <CheckIcon
                                    size="small"
                                    type="brand"
                                    styles={{ visibility: isActive ? 'visible' : 'hidden' }}
                                />
                                <P noDefaultMargin>{label}</P>
                            </ClickableOpacity>
                        );
                    })}
                </Section>
            )}
        </div>
    );
};

export default OrgSwitcher;
